/**
 * DemoResults.tsx
 * V-SAT 2025 exam results with comprehensive scoring breakdown
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy,
  Target,
  Clock,
  CheckCircle2,
  BarChart3,
  RefreshCw,
  ArrowLeft,
  Share2,
  Download,
  Lightbulb,
  TrendingUp,
  Award,
  Star,
  PieChart,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import { Card, CardContent, CardHeader, CardTitle } from '@vsat/shared/components';
import { Progress } from '@vsat/shared/components';
import type { DemoResultSummary, DemoQuestion, DemoAnswerMap } from './types';
import { toast } from 'sonner';

interface Props {
  summary: DemoResultSummary;
  questions: DemoQuestion[];
  answers: DemoAnswerMap;
  onTryAgain: () => void;
  onBackHome: () => void;
}

export default function DemoResults({
  summary,
  questions,
  answers,
  onTryAgain,
  onBackHome,
}: Props) {
  const percentage = Math.round((summary.earnedPoints / summary.totalPoints) * 100);
  const correctAnswers = summary.items.filter(item => item.correct).length;
  const timeSpent = Math.round(summary.durationSeconds / 60);

  // Check if there are any essay questions
  const hasEssayQuestions = questions.some(q => q.type === 'essay');
  const answeredEssayQuestions = questions.filter(
    q => q.type === 'essay' && answers[q.id] && answers[q.id].value
  ).length;

  // V-SAT 2025 Subject breakdown
  const subjectData = useMemo(() => {
    const subjectMap = new Map<
      string,
      { correct: number; total: number; earned: number; maxPoints: number }
    >();

    questions.forEach(q => {
      const subject = q.subject || 'Tổng hợp';
      const item = summary.items.find(i => i.questionId === q.id);

      if (!subjectMap.has(subject)) {
        subjectMap.set(subject, { correct: 0, total: 0, earned: 0, maxPoints: 0 });
      }

      const data = subjectMap.get(subject)!;
      data.total += 1;
      data.maxPoints += q.points;
      if (item) {
        if (item.correct) data.correct += 1;
        data.earned += item.earned;
      }
    });

    return Array.from(subjectMap.entries()).map(([subject, data]) => ({
      subject,
      correct: data.correct,
      total: data.total,
      earned: data.earned,
      maxPoints: data.maxPoints,
      accuracy: Math.round((data.earned / data.maxPoints) * 100),
    }));
  }, [questions, summary.items]);

  // V-SAT 2025 Question type breakdown
  const questionTypeData = useMemo(() => {
    const breakdown = summary.breakdown || {
      trueFalse: { total: 0, earned: 0 },
      multipleChoice: { total: 0, earned: 0 },
      matching: { total: 0, earned: 0 },
      shortAnswer: { total: 0, earned: 0 },
      essay: { total: 0, earned: 0 },
    };

    return [
      {
        type: 'Đúng/Sai (1,2,3,6 điểm)',
        earned: breakdown.trueFalse.earned,
        total: breakdown.trueFalse.total,
        accuracy:
          breakdown.trueFalse.total > 0
            ? Math.round((breakdown.trueFalse.earned / breakdown.trueFalse.total) * 100)
            : 0,
        color: 'bg-blue-500',
      },
      {
        type: 'Trắc nghiệm (6 điểm)',
        earned: breakdown.multipleChoice.earned,
        total: breakdown.multipleChoice.total,
        accuracy:
          breakdown.multipleChoice.total > 0
            ? Math.round((breakdown.multipleChoice.earned / breakdown.multipleChoice.total) * 100)
            : 0,
        color: 'bg-green-500',
      },
      {
        type: 'Ghép hợp (1.5 điểm/cặp)',
        earned: breakdown.matching.earned,
        total: breakdown.matching.total,
        accuracy:
          breakdown.matching.total > 0
            ? Math.round((breakdown.matching.earned / breakdown.matching.total) * 100)
            : 0,
        color: 'bg-purple-500',
      },
      {
        type: 'Trả lời ngắn (6 điểm)',
        earned: breakdown.shortAnswer.earned,
        total: breakdown.shortAnswer.total,
        accuracy:
          breakdown.shortAnswer.total > 0
            ? Math.round((breakdown.shortAnswer.earned / breakdown.shortAnswer.total) * 100)
            : 0,
        color: 'bg-orange-500',
      },
      {
        type: 'Tự luận',
        earned: breakdown.essay.earned,
        total: breakdown.essay.total,
        accuracy:
          breakdown.essay.total > 0
            ? Math.round((breakdown.essay.earned / breakdown.essay.total) * 100)
            : 0,
        color: 'bg-red-500',
      },
    ].filter(item => item.total > 0);
  }, [summary.breakdown]);

  // Performance level
  const getPerformanceLevel = (score: number) => {
    if (score >= 90)
      return { level: 'Xuất sắc', color: 'text-green-600', bg: 'bg-green-50', icon: Trophy };
    if (score >= 80) return { level: 'Giỏi', color: 'text-info', bg: 'bg-blue-50', icon: Award };
    if (score >= 70)
      return { level: 'Khá', color: 'text-purple-600', bg: 'bg-purple-50', icon: Star };
    if (score >= 60)
      return { level: 'Trung bình', color: 'text-orange-600', bg: 'bg-orange-50', icon: Target };
    return { level: 'Cần cải thiện', color: 'text-red-600', bg: 'bg-red-50', icon: TrendingUp };
  };

  const performance = getPerformanceLevel(percentage);
  const PerformanceIcon = performance.icon;

  const handleShare = () => {
    const text = `Tôi vừa hoàn thành bài thi V-SAT 2025 với điểm số ${summary.earnedPoints}/${summary.totalPoints} (${percentage}%)! 🎯`;
    if (navigator.share) {
      navigator.share({ title: 'Kết quả V-SAT 2025', text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Đã sao chép kết quả vào clipboard!');
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 p-4'>
      <div className='max-w-6xl mx-auto space-y-6'>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className='text-center space-y-4'
        >
          <div className='flex items-center justify-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/25'>
              <PerformanceIcon className='w-6 h-6 text-white' />
            </div>
            <div>
              <h1 className='text-3xl font-bold text-foreground'>Kết quả V-SAT 2025</h1>
              <p className='text-muted-foreground'>Bài thi thử minh họa</p>
            </div>
          </div>
        </motion.div>

        {/* Main Score Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Card className='border-0 shadow-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
            <CardContent className='p-8'>
              <div className='flex flex-col items-center space-y-6'>
                {/* Score Display */}
                <div className='text-center'>
                  <div className='text-6xl font-bold text-primary mb-2'>{summary.earnedPoints}</div>
                  <div className='text-lg text-muted-foreground'>/ {summary.totalPoints} điểm</div>
                </div>

                {/* Person Name */}
                <div className='text-center space-y-2'>
                  <h2 className='text-2xl font-bold text-foreground'>Nguyễn Văn A</h2>
                  <div className='flex items-center justify-center gap-2 text-muted-foreground'>
                    <span className='text-lg'>Môn Tiếng Anh V-SAT 2025</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Essay Questions Notification */}
        {hasEssayQuestions && answeredEssayQuestions > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className='border-purple-200 bg-purple-50/50 dark:bg-purple-900/20'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <div className='w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center flex-shrink-0'>
                    <AlertCircle className='w-6 h-6 text-purple-600 dark:text-purple-400' />
                  </div>
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2'>
                      Câu hỏi tự luận chưa được chấm điểm
                    </h3>
                    <p className='text-sm text-purple-700 dark:text-purple-300 mb-3'>
                      Bạn đã hoàn thành <strong>{answeredEssayQuestions} câu hỏi tự luận</strong>.
                      Những câu hỏi này sẽ được giáo viên chấm điểm thủ công và cập nhật kết quả
                      sau.
                    </p>
                    <div className='flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400'>
                      <Clock className='w-4 h-4' />
                      <span>Thời gian chấm điểm: 1-3 ngày làm việc</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className='flex flex-wrap gap-4 justify-center'
        >
          <Button
            onClick={onTryAgain}
            size='lg'
            className='gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg shadow-primary/25 transition-all duration-300'
          >
            <RefreshCw className='w-4 h-4' />
            Làm lại
          </Button>

          <Button
            onClick={onBackHome}
            variant='outline'
            size='lg'
            className='gap-2 border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
          >
            <ArrowLeft className='w-4 h-4' />
            Về trang chủ
          </Button>

          <Button
            onClick={handleShare}
            variant='outline'
            size='lg'
            className='gap-2 border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
          >
            <Share2 className='w-4 h-4' />
            Chia sẻ
          </Button>
        </motion.div>

        {/* V-SAT 2025 Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className='text-center'
        >
          <Card className='bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20 rounded-2xl shadow-xl border border-primary/50'>
            <CardContent className='p-6'>
              <div className='flex items-center justify-center gap-2 mb-3'>
                <div className='w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/25'>
                  <Lightbulb className='w-4 h-4 text-white' />
                </div>
                <h3 className='font-semibold text-foreground'>Về V-SAT 2025</h3>
              </div>
              <p className='text-sm text-muted-foreground max-w-2xl mx-auto'>
                V-SAT 2025 là kỳ thi đánh giá năng lực của Bộ Giáo dục với cấu trúc mới: 25 câu
                hỏi/môn, tổng 150 điểm. Theo quy định chính thức: Đúng/Sai (1,2,3,6 điểm), Trắc
                nghiệm (6 điểm/câu), Ghép hợp (1.5 điểm/cặp, 6 điểm nếu đúng 4/4), Trả lời ngắn (6
                điểm/câu).
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
